import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import{ 
  Cart,
  Drug, 
  MedicalConsultation,
  MedicalTest, 
  CartQuantityItem,
  Order, 
  User, 
  QuantityDetails
} from '../../models';
import { 
  AuthenticationService, 
  CartService, 
  DataService,
  DocumentService, 
  MedicalTestService,
  OrderService,
  PharmacyService
} from '../../services';
import { catchError, first, map, mergeMap, tap } from 'rxjs/operators';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import { of, pipe } from 'rxjs';

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss']
})
export class CartComponent implements OnInit {
  cartItems: (MedicalTest | Drug)[] = [];
  user: User;
  recommendedMedicalTests: MedicalTest[] = [];
  recommendedDrugs: Drug[] = [];
  medicalTests: MedicalTest[] = [];
  drugs: Drug[] = [];
  slickCarouselConfig: any;
  addConsultation: boolean;
  consultationAmount: number;
  productQuantities: CartQuantityItem[] = [];
  cartItemQuantity: number = 1;
  productQuantitiesArray: number[];
  totalCartCost: number;
  isDrug: boolean;
  selectedQuantities: { [drugId: string]: any } = {};
  cartItemName: { [id: number]: string } = {};
  itemQuantities: { [id: number]: number } = {};
  itemAmounts: { [id: number]: string } = {};

  constructor (
    private router: Router,
    private authenticationService: AuthenticationService,
    private cartService: CartService,
    private dataService: DataService,
    private documentService: DocumentService,
    private medicalTestService: MedicalTestService,
    private orderService: OrderService,
    private pharmacyService: PharmacyService
  ) { }

  ngOnInit(): void {
    this.slickCarouselConfig = {
      ...this.documentService.getslickCarouselConfig(),
      infinite: false,
      slidesToShow: 1,
      slidesToScroll: 1
    };
    this.user = this.authenticationService.currentUser()['user'];
  
    this.medicalTestService.getLocalMedicalConsultationAmount().pipe(
      tap((response: MedicalConsultation) => {
        this.consultationAmount = Number(response?.amount) || 0;
        this.dataService.saveLocalMedicalConsultationAmount(this.consultationAmount);
        this.dataService.medicalConsultationAmountSubject.next(this.consultationAmount);
      }),
      mergeMap(() => this.cartService.getCartProducts()),
      tap(cartMedicalTests => {
        // get localStorage cartItems when user refreshes page
        if (!cartMedicalTests || cartMedicalTests.length === 0) {
          this.cartItems = JSON.parse(localStorage.getItem('cartItems') || '[]');
        } else {
          this.cartItems = cartMedicalTests;
        }
        this.cartItems.map(item => this.setItemName(item));
        this.isDrug = this.cartItems.length > 0 && 'country_of_origin' in this.cartItems[0];
        this.getProductRecommendations(this.cartItems);
      }),
      mergeMap(() => this.dataService.getLocalCartItemQuantities()),
      tap(quantities => {
        this.productQuantities = Array.isArray(quantities) ? quantities : [];
        this.productQuantitiesArray = this.cartItems.map(item =>
          this.productQuantities.find(quantity =>
            quantity.productId === item.id)?.quantity || 1
        );
        if (this.isDrug) {
          this.getCartSelectedQuantities(this.cartItems as Drug[]);
        }
        this.setItemQuantitiesAndAmounts();
      }),
      mergeMap(() => this.dataService.getLocalConsultationStatus().pipe(first()))
    ).subscribe(status => {
      this.addConsultation = status;
      this.totalCartCost = this.cartItems.length < 0 ? 0 : this.getTotalCost();
      localStorage.setItem('totalCartCost', JSON.stringify(this.totalCartCost));
    });
  }

  setItemName(item: MedicalTest | Drug): void {
    const name = this.dataService.getItemName(item);
    this.cartItemName[item.id] = name;
  }

  setItemQuantitiesAndAmounts(): void {
    this.itemQuantities = {};
    this.itemAmounts = {};
    this.cartItems.map(item => {
      const matched = this.productQuantities.find(q => q.productId === item.id);
      this.itemQuantities[item.id] = matched?.quantity || 1;
      this.itemAmounts[item.id] = matched?.amount || (item as MedicalTest).price || '0';
    });
  }

  getSelectedQuantityType(item: MedicalTest | Drug): string {
    const matched = this.productQuantities.find(q => q.productId === item.id);
    return matched?.quantity_type || 'N/A';
  }
  
  getSelectedAmount(item: MedicalTest | Drug): string {
    const matched = this.productQuantities.find(q => q.productId === item.id);
    return matched?.amount || '0';
  }

  onQuantityTypeChange(item: Drug, selectedType: QuantityDetails): void {
    this.selectedQuantities[item.id] = selectedType;
    const index = this.productQuantities.findIndex(q => q.productId === item.id);
    const quantity = this.productQuantities[index]?.quantity || 1;
    const updatedItem: CartQuantityItem = {
      productId: item.id,
      quantity: quantity,
      quantity_type: selectedType.name,  
      amount: selectedType.selling_price || '0',
      quantityTypeId: selectedType.id
    };
    if (index !== -1) {
      this.productQuantities[index] = updatedItem;
    } else {
      this.productQuantities.push(updatedItem);
    }
    this.dataService.saveLocalCartItemQuantities(this.productQuantities);
  }

  setSelectedQuantities(products: Drug[]): void {
    this.productQuantities = JSON.parse(localStorage.getItem('cartItemQuantities') || '[]');
    for (const drug of products) {
      if (Array.isArray(drug.quantity_types) && drug.quantity_types.length > 0) {
        const existingCartItem = this.productQuantities.find(product => product.productId === drug.id);
        const matchedType = drug.quantity_types.find(quantity => quantity.name === existingCartItem?.quantity_type);
        this.selectedQuantities[drug.id] = matchedType || drug.quantity_types[0];
      } else {
        this.selectedQuantities[drug.id] = null;
      }
    }
  }

  getCartSelectedQuantities(product: Drug[]): void {
    this.selectedQuantities = {};
    this.setSelectedQuantities(product);
  }
  
  increaseQuantity(product: MedicalTest | Drug): void {
    const currentQuantity = this.itemQuantities[product.id];
    this.updateProductQuantities(product.id, currentQuantity + 1);
  }
  
  decreaseQuantity(product: MedicalTest | Drug): void {
    const currentQuantity = this.itemQuantities[product.id];
    if (currentQuantity > 1) {
      this.updateProductQuantities(product.id, currentQuantity - 1);
    }    
  }  

  updateProductQuantities(productId: number, newQuantity: number): void {
    let updatedQuantities = [...this.productQuantities];
    const index = updatedQuantities.findIndex(q => q.productId === productId);
    if (index !== -1) {
      if (newQuantity <= 0) {
        updatedQuantities = updatedQuantities.filter(q => q.productId !== productId);
      } else {
        updatedQuantities[index].quantity = newQuantity;
      }
    } else if (newQuantity > 0) {
      updatedQuantities.push({ productId, quantity: newQuantity });
    }
    this.productQuantities = updatedQuantities;
    this.dataService.saveLocalCartItemQuantities(updatedQuantities);
    this.setItemQuantitiesAndAmounts(); 
  }
  
  removeFromCart(item: MedicalTest | Drug): void {
    this.cartService.removeFromCart(item);
    this.updateProductQuantities(item.id, 0);
    const storedCart = JSON.parse(localStorage.getItem('cartItems') || '[]') as (Drug | MedicalTest)[];
    const updatedCart = storedCart.filter(cartItem => cartItem.id !== item.id);
    localStorage.setItem('cartItems', JSON.stringify(updatedCart));
    this.dataService.updateCartItems(updatedCart);
    this.cartItems = updatedCart;
    this.getCartSelectedQuantities(this.cartItems as Drug[]);
    //refresh badge count
    this.dataService.cartUpdatedSubject.next();
  }

  getTotalCost(): number {
    return this.dataService.getCartTotalCost({
      items: this.cartItems,
      itemQuantities: this.itemQuantities,
      itemAmounts: this.itemAmounts,
      isDrug: this.isDrug,
      addConsultation: this.addConsultation,
      consultationAmount: this.consultationAmount
    });
  }

  removeConsultationFromCart() {
    this.addConsultation = false; 
    this.dataService.saveLocalAddConsultationStatus(this.addConsultation);
    this.totalCartCost = this.getTotalCost();
    this.dataService.saveToLocalStorage('totalCartCost', this.totalCartCost); 
  }
    
  getMedicalTestsRecommendations() {
    this.dataService.getAllLocalMedicalTests().pipe(first(),
      mergeMap(medicalTests => {
        this.medicalTests = medicalTests;
        if (!this.medicalTests.length) {
          return []; 
        }
        const formData = new FormData();
        formData.append('productIds[]', this.cartItems.map(item => item.id).toString());
        return this.medicalTestService.getRecommendedMedicalTests(formData);
      })
    ).subscribe(response => {
      this.recommendedMedicalTests = response as MedicalTest[];
    });
  }

  getProductRecommendations(products: (MedicalTest | Drug)[]) {
    if (this.isDrug) {
    this.pharmacyService.getDrugs().pipe(first(),
      mergeMap(drugs => {
      this.drugs = drugs;
      if (!this.drugs.length) {
        return []; 
      }
      const formData = new FormData();
      formData.append('productIds[]', this.cartItems.map(item => item.id).toString());
      return this.pharmacyService.getRecommendedDrugs(formData);
      })
      ).subscribe(response => {
        this.recommendedDrugs = response as Drug[]; 
        this.setSelectedQuantities(this.recommendedDrugs);
      });
    } else {
      this.medicalTestService.getMedicalTests().pipe(first(),
        mergeMap(medicalTests => {
          this.medicalTests = medicalTests;
          if (!this.medicalTests.length) {
            return []; 
          }
          const formData = new FormData();
          formData.append('productIds[]', this.cartItems.map(item => item.id).toString());
          return this.medicalTestService.getRecommendedMedicalTests(formData);
        })
      ).subscribe(response => {
        this.recommendedMedicalTests = response as MedicalTest[];
      });
    }
  }

  addToCart(product: MedicalTest | Drug, selectedQuantity?: QuantityDetails) {
    const cartHasDrug= this.cartItems.some(item => ('country_of_origin' in item));
    const isDrug = 'country_of_origin' in product;
    if (cartHasDrug && !isDrug) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'You can only add either a medical test or a drug to the cart at a time.',
        confirmButtonColor: '#f37b7b',
      });
      return;
    }
    if (isDrug) {
      if (product.quantity_types && product.quantity_types.length > 0) {
        selectedQuantity = product.quantity_types[0];
      }
    }
    this.dataService.addProductToCart(product, selectedQuantity);
  }

  goToMedicalTestDetails(medicalTest: MedicalTest) {
    this.dataService.saveLocalDataToView(medicalTest);
    this.router.navigate(['medical-test-details']);
  }

  goToDrugDetails(drug: Drug) {
    this.dataService.saveLocalDataToView(drug);
    this.router.navigate(['single-drug-details']);
  }

  checkout() {
    const formData = new FormData();
    if (this.isDrug) {
      formData.append('drugIds[]', 
        (this.cartItems.length === 0 && this.addConsultation) ? 
          '' : this.cartItems.map(item => item.id).toString());
      const medicalTestIds: MedicalTest[] = [];
      formData.append('medicalTestIds[]', '');
    } else {
      formData.append('medicalTestIds[]', 
        (this.cartItems.length === 0 && this.addConsultation) ? 
          '' : this.cartItems.map(item => item.id).toString());
      const drugIds: Drug[] = [];;
      formData.append('drugIds[]', '');
    }
    const quantities = JSON.parse(localStorage.getItem('cartItemQuantities') || '[]') as {
      productId: number;
      quantity: number;
      quantity_type: string;
      quantityTypeId: number;
    }[];
    formData.append('selectedQuantityTypeIds[]', quantities.map(q => q.quantityTypeId).toString());
    formData.append('quantity[]', quantities.map(q => q.quantity).toString());
    formData.append('medicalConsultation', this.addConsultation.toString()); 
    const popUpConfig: SweetAlertOptions = {
      title: 'An error occurred',
      html: 'Please contact us at: <b>info@go-checks.com</b> or via the contact form',
      icon: 'error',
      confirmButtonColor: '#f37b7b',
      confirmButtonText: 'Ok',
      showCancelButton: false,
      cancelButtonColor: '#f37b7b',
      cancelButtonText: 'Try Again'
    };
    this.orderService.getMyOrders(this.user.id).subscribe((orders) => {
      const userOrders = orders as Order[];
      const ongoingOrders = userOrders.filter(
        (order) => order.status !== 'Order Complete'
      );
      // Determine type of each ongoing order
      const hasOngoingMedicalTestOrder = ongoingOrders.some(order => (order.medical_tests?.length ?? 0) > 0);
      const hasOngoingDrugOrder = ongoingOrders.some(order => (order.drugs?.length ?? 0) > 0);
      // Order being created
      const isCreatingDrugOrder = this.isDrug;
      const isCreatingMedicalTestOrder = !this.isDrug;
      if (
        (hasOngoingMedicalTestOrder && hasOngoingDrugOrder) || // both ongoing
        (isCreatingMedicalTestOrder && hasOngoingMedicalTestOrder && !hasOngoingDrugOrder) ||
        (isCreatingDrugOrder && hasOngoingDrugOrder && !hasOngoingMedicalTestOrder)
      ) {
        // Display a warning message if there are ongoing orders
        popUpConfig.title = 'Ongoing Orders';
        popUpConfig.html = 'You have ongoing orders that need to be completed before placing a new order.';
        popUpConfig.icon = 'warning';
        Swal.fire(popUpConfig);
        return;
      }

      // check what error message one gets when a user updates cart without an existing

      // Proceed with checkout if there are no ongoing orders
      if (this.cartItems.length > 0 || this.addConsultation) {
        this.cartService.getUserCart(this.user.id).pipe(
          mergeMap(response => {
            if (response.message === 'No associated cart') {
              return this.cartService.createCart(formData);
            } else {
              this.dataService.saveToLocalStorage('cartItems', this.cartItems);
              this.dataService.saveToLocalStorage('quantities', this.productQuantitiesArray);
              return this.cartService.updateCart(this.user.id as number, formData);
            }
          }), catchError(error => {
            Swal.fire(popUpConfig);
            return of(error);
          })
        ).subscribe(
        (response: any) => { 
          if (response.message === 'Cart updated' || (response.id && response.medical_test_ids)) {
            this.router.navigate(['add-order']);
          } else if (response.message === 'Error when updating cart') {
            Swal.fire(popUpConfig).then(() => {
              this.router.navigate(['dashboard']);
            });;
          }
        }, error => {
          Swal.fire(popUpConfig).then(() => {
            this.router.navigate(['dashboard']);
          });;
        });
      } else {
          Swal.fire({
            title: '',
            html: '<h4>Your Cart</h4>There are no items in your cart',
            icon: 'info',
            confirmButtonColor: 'var(--thm-base)',
            customClass: {
              icon: 'sweet-alert-icon-class'
            },
            showClass: {
              popup: 'sweet-alert-slow-animation-show'
            },
            hideClass: {
              popup: 'sweet-alert-slow-animation-hide'
            }
          }).then(() => {
            this.router.navigate(['medical-tests']);
          });
      };
    });
  }
  
}
